<template>
  <div class="receivables-table-wrap">
    <b-table
      :items="mapped"
      :fields="fields"
      responsive
      hover
      head-variant="light"
      small
      show-empty
      table-class="yc-receivables-table"
      :tbody-tr-class="rowClassForReceivable"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-compare="sorter"
    >
    <template #cell(amount)="d">
      <div class="yc-amount-cell">
        <span class="yc-money yc-money-strong">
          $ {{ money(d.item.amount) }}
        </span>
        <small
          v-if="hasFiscalDifference(d.item)"
          class="yc-fiscal-difference"
          :title="fiscalDifferenceTitle(d.item)"
        >
          Cotización $ {{ money(d.item.commercial_amount) }} · Dif. {{ signedMoney(d.item.fiscal_amount_difference) }}
        </small>
      </div>
    </template>

    <template #cell(rep_select)="d">
      <div class="yc-rep-select-cell">
        <b-form-checkbox
          :checked="isSelectedForRep(d.item.quoteId)"
          :disabled="!isRepSelectable(d.item)"
          :title="repSelectionDisabledReason(d.item)"
          class="mb-0"
          @change="toggleRepSelection(d.item, $event)"
        />

        <div
          v-if="isSelectedForRep(d.item.quoteId)"
          class="yc-rep-amount-editor"
        >
          <currency-input
            :value="repAmounts[d.item.quoteId]"
            size="sm"
            min="0"
            :max="repBalanceFor(d.item)"
            :precision="2"
            class="yc-rep-amount-input"
            placeholder="Monto a aplicar"
            @input="onRepAmountInput(d.item, $event)"
          />
          <b-button
            size="sm"
            variant="link"
            class="yc-inline-link yc-rep-amount-link"
            :disabled="isFullBalanceApplied(d.item)"
            @click="applyFullRepBalance(d.item)"
          >
            Saldo total
          </b-button>
          <small
            v-if="!isValidRepAmount(d.item)"
            class="yc-rep-amount-warning"
          >
            {{ repAmountWarning(d.item) }}
          </small>
        </div>
      </div>
    </template>

    <template #cell(paid_amount)="d">
      <span class="yc-money">
        $ {{ money(d.item.paid_amount) }}
      </span>
    </template>

    <template #cell(sale_date)="d">
      <div class="yc-date-cell">
        <span class="yc-date-main">{{ formatDate(d.item.sale_date) }}</span>
      </div>
    </template>

    <template #cell(review_date)="d">
      <div class="yc-date-cell">
        <span class="yc-date-main">{{ formatDate(d.item.review_date) }}</span>
      </div>
    </template>

    <template #cell(due_date)="d">
      <div class="yc-date-cell">
        <span class="yc-date-main">{{ formatDate(d.item.due_date) }}</span>
      </div>
    </template>

    <template #cell(balance)="d">
      <div class="yc-balance-cell">
        <span
          class="yc-money"
          :class="{
            'text-success': d.item.paid_in_full,
            'text-danger': !d.item.paid_in_full && d.item.days_overdue > 0,
            'text-body': !d.item.paid_in_full && d.item.days_overdue <= 0
          }"
        >
          $ {{ money(d.item.balance) }}
        </span>
        <small
          v-if="showsPendingComplement(d.item)"
          class="yc-pending-complement no-print"
          title="La factura ya está cobrada, pero su CFDI PPD sigue sin complemento de pago"
        >
          Falta REP $ {{ money(repBalanceFor(d.item)) }}
        </small>
      </div>
    </template>

    <template #cell(days_overdue)="d">
      <span
        class="yc-aging-pill"
        :class="{
          'text-success': d.item.paid_in_full,
          'is-overdue': d.item.days_overdue > 0,
          'is-current': d.item.days_overdue <= 0
        }"
      >
        {{ overdueLabel(d.item) }}
      </span>
    </template>
    <!-- FOLIO: botón que navega a la OC con prefijo F -->
    <template #cell(folio)="d">
      <div class="yc-folio-cell">
        <!-- Pantalla -->
        <b-button
          size="sm"
          variant="link"
          class="yc-folio-pill no-print"
          :to="handleRouter(d.item)"
        >
          {{ d.item.folio ? `F${d.item.folio}` : 'F—' }}
        </b-button>
        <!-- Impresión -->
        <span class="print-only font-weight-bolder">
          {{ d.item.folio ? `F${d.item.folio}` : 'F—' }}
        </span>
      </div>
    </template>

    <template #cell(remission)="d">
      <div class="d-flex align-items-center yc-remission-cell">
        <b-form-checkbox
          :checked="d.item.has_complete_remissions"
          disabled
          class="mb-0"
        />
        <small
          class="yc-remission-text"
          :title="remissionTitle(d.item)"
        >
          {{ d.item.has_complete_remissions ? 'Completa' : 'Pendiente' }}
        </small>
      </div>
    </template>

    <template #cell(checkplus_reference)="d">
      <div
        v-if="isEditing(d.item)"
        class="yc-invoice-editor"
      >
        <b-form-input
          v-model.trim="checkplusDraft[d.item.quoteId]"
          size="sm"
          placeholder="Factura"
          class="yc-invoice-input"
        />
        <b-button
          size="sm"
          variant="primary"
          class="yc-action-btn"
          :disabled="!checkplusDraft[d.item.quoteId]"
          @click="save(d.item)"
        >
          Guardar
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="yc-action-btn"
          @click="cancel(d.item)"
        >
          Cancelar
        </b-button>
      </div>

      <div
        v-else
        class="yc-inline-meta"
      >
        <span
          v-if="d.item.checkplus_reference"
          class="yc-inline-value"
        >{{ d.item.checkplus_reference }}</span>
        <span
          v-else
          class="text-muted yc-inline-value"
        >—</span>
        <b-button
          size="sm"
          variant="link"
          class="yc-inline-link"
          @click="start(d.item)"
        >
          {{ d.item.checkplus_reference ? 'Editar' : 'Agregar' }}
        </b-button>
      </div>
    </template>

    <template #cell(invoice_filename)="d">
      <span
        v-if="d.item.invoice_filename"
        class="yc-file-name"
        :title="d.item.invoice_filename"
      >{{ d.item.invoice_filename }}</span>
      <span
        v-else
        class="text-muted"
      >—</span>
    </template>

    <template #cell(actions)="d">
      <div
        v-if="!d.item.paid_in_full"
        class="yc-actions"
      >
        <currency-input
          :value="partialAmounts[d.item.quoteId] || null"
          size="sm"
          min="0"
          :precision="2"
          class="yc-amount-input"
          placeholder="Monto"
          @input="onPartialInput(d.item.quoteId, $event)"
        />

        <b-button
          size="sm"
          variant="success"
          class="yc-action-btn"
          :disabled="!canPayPartial(d.item)"
          @click="payPartial(d.item)"
        >
          Pagar
        </b-button>

        <b-button
          size="sm"
          variant="outline-success"
          class="yc-action-btn"
          @click="$emit('mark-paid', d.item.quoteId)"
        >
          Pagar todo
        </b-button>
      </div>

      <b-button
        v-else
        size="sm"
        variant="outline-danger"
        class="yc-action-btn"
        @click="$emit('unmark-paid', d.item.quoteId)"
      >
        Revertir pago
      </b-button>
    </template>
    </b-table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import resolveQuoteRoute from '@/utils/quoteRoute'
import CurrencyInput from '@/components/CurrencyInput.vue'

export default {
  components: { CurrencyInput },
  props: {
    rows: { type: Array, default: () => [] },
    selectedTaxInformationId: { type: [String, Number], default: '' },
    reservedRepQuoteIds: { type: Array, default: () => [] },
    allowedReservedRepQuoteIds: { type: Array, default: () => [] },
    repUnassignedAmount: { type: Number, default: 0 },
    repPaymentAmount: { type: Number, default: 0 },
  },
  data() {
    return {
      editing: {},
      checkplusDraft: {},
      partialAmounts: {},
      repSelectedQuoteIds: [],
      repAmounts: {},
      sortBy: 'folio',
      sortDesc: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    isCustomerUser() {
      return this.currentUser?.role_name === 'customer'
    },
    fields() {
      const fields = [
        { key: 'project', label: 'Obra' },
        { key: 'sale_date', label: 'Venta', sortable: true },
        { key: 'review_date', label: 'Revisión', sortable: true },
        { key: 'due_date', label: 'Vencimiento', sortable: true },
        { key: 'folio', label: 'Folio', sortable: true },
        { key: 'remission', label: 'Remisión', sortable: true },

        // Checkplus: visible en pantalla, oculto en impresión
        {
          key: 'checkplus_reference', label: 'Checkplus', thClass: 'no-print', tdClass: 'no-print',
        },

        {
          key: 'invoice_filename',
          label: 'Factura',
          thClass: 'yc-invoice-file-col',
          tdClass: 'yc-invoice-file-col',
        },
        { key: 'amount', label: 'Importe', class: 'text-right' },
        { key: 'paid_amount', label: 'Pagado', class: 'text-right' },
        { key: 'balance', label: 'Saldo', class: 'text-right' },
        { key: 'days_overdue', label: 'Días venc.', sortable: true },
        {
          key: 'rep_select',
          label: 'REP',
          thStyle: { width: '170px' },
          thClass: 'no-print',
          tdClass: 'no-print',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '280px' },
          thClass: 'no-print',
          tdClass: 'no-print',
        },
      ]

      return this.isCustomerUser
        ? fields.filter(field => !['rep_select', 'checkplus_reference', 'actions'].includes(field.key))
        : fields
    },
    mapped() {
      return (this.rows || []).map(r => ({
        quoteId: r.id,
        id: r.id,
        customer: r.customer?.name,
        project: r.project?.name,
        sale_date: r.sale_date,
        review_date: r.review_date,
        due_date: r.due_date,
        folio: r.folio,
        quote_status: r.quote_status,
        payment_status: r.payment_status,
        is_published: r.is_published,
        checkplus_reference: r.checkplus_reference,
        invoice_filenames: this.normalizeInvoiceFilenames(r),
        invoice_filename: this.primaryInvoiceFilename(r),
        tax_information: r.tax_information,
        facturapi_invoice_id: r.facturapi_invoice_id,
        facturapi_invoice_uuid: r.facturapi_invoice_uuid,
        facturapi_payment_invoice_id: r.facturapi_payment_invoice_id,
        facturapi_payment_method: r.facturapi_payment_method,
        has_complete_remissions: !!r.has_complete_remissions,
        remitted_purchase_orders_count: Number(r.remitted_purchase_orders_count || 0),
        total_purchase_orders: Number(r.total_purchase_orders || 0),
        remission: Number(r.has_complete_remissions ? 1 : 0),

        amount: r.amount,
        commercial_amount: r.commercial_amount,
        fiscal_amount_difference: r.fiscal_amount_difference,
        paid_amount: r.paid_amount,
        balance: r.balance,
        paid_in_full: r.paid_in_full,
        pending_complement_amount: r.pending_complement_amount,
        days_overdue: r.days_overdue,
      }))
    },
    repSelectedRows() {
      return this.mapped
        .filter(row => this.repSelectedQuoteIds.includes(String(row.quoteId)))
        .map(row => ({ ...row, repAmount: this.repAmountFor(row), repBalance: this.repBalanceFor(row) }))
    },
  },
  watch: {
    rows() {
      const visibleIds = this.mapped.map(row => String(row.quoteId))
      const next = this.repSelectedQuoteIds.filter(quoteId => visibleIds.includes(quoteId))
      if (next.length !== this.repSelectedQuoteIds.length) {
        Object.keys(this.repAmounts)
          .filter(quoteId => !next.includes(String(quoteId)))
          .forEach(quoteId => this.$delete(this.repAmounts, quoteId))
        this.repSelectedQuoteIds = next
        this.$emit('rep-selection-change', this.repSelectedRows)
      }
    },
  },
  methods: {
    isSelectedForRep(quoteId) {
      return this.repSelectedQuoteIds.includes(String(quoteId))
    },
    // El tope del REP es lo que falta amparar fiscalmente, no lo que falta
    // cobrar: una factura PPD ya cobrada sigue debiendo su complemento.
    repBalanceFor(item) {
      const pending = Number(item?.pending_complement_amount)
      if (Number.isFinite(pending)) return pending

      return Number(item?.balance || 0)
    },
    // El complemento pendiente es interno: el cliente no lo ve ni sale impreso
    // en su estado de cuenta, que es de saldos por cobrar.
    showsPendingComplement(item) {
      return !this.isCustomerUser && item.paid_in_full && this.repBalanceFor(item) > 0
    },
    rowClassForReceivable(item, type) {
      if (type !== 'row' || !item) return ''

      return this.showsPendingComplement(item) ? 'yc-complement-only-row' : ''
    },
    isRepSelectable(item) {
      return Boolean(
        this.selectedTaxInformationId
        && this.repBalanceFor(item) > 0
        && item.tax_information?.id === this.selectedTaxInformationId
        && item.facturapi_invoice_id
        && item.facturapi_invoice_uuid
        && item.facturapi_payment_method === 'PPD'
        && !item.facturapi_payment_invoice_id
        && !this.isReservedForAnotherPendingRep(item.quoteId),
      )
    },
    repSelectionDisabledReason(item) {
      if (!this.selectedTaxInformationId) return 'Selecciona una razón social para el REP'
      if (item.tax_information?.id !== this.selectedTaxInformationId) return 'Esta cotización fue facturada con otra razón social'
      if (!item.facturapi_invoice_id || !item.facturapi_invoice_uuid) return 'La cotización aún no tiene factura Facturapi'
      if (item.facturapi_payment_method !== 'PPD') return 'La factura no es PPD'
      if (item.facturapi_payment_invoice_id) return 'La cotización ya tiene REP'
      if (this.repBalanceFor(item) <= 0) return 'La factura ya está amparada por completo con complementos de pago'
      if (this.isReservedForAnotherPendingRep(item.quoteId)) return 'La cotización ya está asociada a otro pago guardado pendiente'

      return ''
    },
    isReservedForAnotherPendingRep(quoteId) {
      return this.reservedRepQuoteIds.includes(String(quoteId))
        && !this.allowedReservedRepQuoteIds.includes(String(quoteId))
    },
    repAmountFor(item) {
      const stored = this.repAmounts[item.quoteId]
      if (stored === null || stored === undefined || stored === '') return 0

      return this.parseAmount(stored)
    },
    defaultRepAmountFor(item) {
      const repBalance = this.repBalanceFor(item)
      if (Number(this.repPaymentAmount || 0) <= 0) return repBalance

      return Math.min(repBalance, Math.max(0, Number(this.repUnassignedAmount || 0)))
    },
    isValidRepAmount(item) {
      const amount = this.repAmountFor(item)
      return amount > 0 && amount <= this.repBalanceFor(item) + 0.005
    },
    repAmountWarning(item) {
      const repBalance = this.repBalanceFor(item)
      if (this.repAmountFor(item) > repBalance + 0.005) {
        return `El pendiente de complemento bajó a $ ${this.money(repBalance)}`
      }

      return `Captura un monto entre $ 0.01 y $ ${this.money(repBalance)}`
    },
    isFullBalanceApplied(item) {
      return Math.abs(this.repAmountFor(item) - this.repBalanceFor(item)) < 0.005
    },
    onRepAmountInput(item, value) {
      this.$set(this.repAmounts, item.quoteId, value)
      this.$emit('rep-selection-change', this.repSelectedRows)
    },
    applyFullRepBalance(item) {
      this.onRepAmountInput(item, this.repBalanceFor(item))
    },
    toggleRepSelection(item, checked) {
      const next = checked
        ? Array.from(new Set(this.repSelectedQuoteIds.concat(String(item.quoteId))))
        : this.repSelectedQuoteIds.filter(quoteId => quoteId !== String(item.quoteId))

      if (checked) {
        this.$set(this.repAmounts, item.quoteId, this.defaultRepAmountFor(item))
      } else {
        this.$delete(this.repAmounts, item.quoteId)
      }

      this.repSelectedQuoteIds = next
      this.$emit('rep-selection-change', this.repSelectedRows)
    },
    clearRepSelection() {
      this.repSelectedQuoteIds = []
      this.repAmounts = {}
      this.$emit('rep-selection-change', [])
    },
    setRepSelection(quoteIds, amounts = {}) {
      const visibleIds = this.mapped.map(row => String(row.quoteId))
      this.repSelectedQuoteIds = Array.from(new Set((quoteIds || [])
        .map(id => String(id))
        .filter(quoteId => visibleIds.includes(quoteId))))

      const nextAmounts = {}
      this.repSelectedQuoteIds.forEach(quoteId => {
        const row = this.mapped.find(item => String(item.quoteId) === quoteId)
        if (!row) return

        const stored = amounts[quoteId]
        nextAmounts[row.quoteId] = stored === undefined || stored === null || stored === ''
          ? this.defaultRepAmountFor(row)
          : Math.min(this.parseAmount(stored), this.repBalanceFor(row))
      })
      this.repAmounts = nextAmounts

      this.$emit('rep-selection-change', this.repSelectedRows)
    },

    isNil(v) {
      return v === null || v === undefined || v === ''
    },
    toNumber(v) {
      if (typeof v === 'number') return v
      const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''))
      return Number.isNaN(n) ? -Infinity : n
    },
    toTime(v) {
      if (!v) return -Infinity

      // YYYY-MM-DD local
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [y, m, d] = v.split('-').map(Number)
        return new Date(y, m - 1, d).getTime()
      }

      const dte = v instanceof Date ? v : new Date(v)
      const t = dte.getTime()
      return Number.isNaN(t) ? -Infinity : t
    },
    numCompare(x, y) {
      if (x < y) return -1
      if (x > y) return 1
      return 0
    },

    // ---- Comparador para b-table ----
    sorter(a, b, key, sortDesc) {
    // nulos/indefinidos: al final en ASC, al inicio en DESC
      if (this.isNil(a[key]) && this.isNil(b[key])) return 0
      if (this.isNil(a[key])) return sortDesc ? -1 : 1
      if (this.isNil(b[key])) return sortDesc ? 1 : -1

      switch (key) {
        case 'folio':
          return this.numCompare(this.toNumber(a.folio), this.toNumber(b.folio))
        case 'days_overdue':
          return this.numCompare(this.toNumber(a.days_overdue), this.toNumber(b.days_overdue))
        case 'remission':
          return this.numCompare(this.toNumber(a.remission), this.toNumber(b.remission))
        case 'sale_date':
        case 'review_date':
        case 'due_date':
          return this.numCompare(this.toTime(a[key]), this.toTime(b[key]))
        default:
        // deja a BootstrapVue el fallback por si hay otro campo sortable
          return null
      }
    },

    money(v) {
      const n = Number(v || 0)
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    signedMoney(v) {
      const n = Number(v || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}$ ${this.money(n)}`
    },
    hasFiscalDifference(item) {
      return Math.abs(Number(item.fiscal_amount_difference || 0)) >= 0.01
    },
    fiscalDifferenceTitle(item) {
      return `Facturapi timbró esta factura por $ ${this.money(item.amount)}; la cotización quedó en $ ${this.money(item.commercial_amount)}. Para REP se usa el total fiscal.`
    },
    normalizeInvoiceFilenames(row) {
      let filenames = []

      if (Array.isArray(row.invoice_filenames)) {
        filenames = row.invoice_filenames
      } else if (row.invoice_filename) {
        filenames = [row.invoice_filename]
      }

      return filenames.filter(Boolean)
    },
    primaryInvoiceFilename(row) {
      const filenames = this.normalizeInvoiceFilenames(row)
      const firstPdf = filenames.find(filename => /\.pdf$/i.test(String(filename)))

      if (firstPdf) return firstPdf
      if (filenames.length) return filenames[0]

      return null
    },
    handleRouter(quote) {
      return resolveQuoteRoute({
        quote,
        currentUser: this.currentUser,
        routeParams: this.$route.params,
      })
    },
    isEditing(item) { return !!this.editing[item.quoteId] },
    start(item) {
      this.$set(this.editing, item.quoteId, true)
      this.$set(this.checkplusDraft, item.quoteId, item.checkplus_reference || '')
    },
    cancel(item) {
      this.$delete(this.editing, item.quoteId)
      this.$delete(this.checkplusDraft, item.quoteId)
    },
    save(item) {
      this.$emit('save-invoice', {
        quoteId: item.quoteId,
        checkplusReference: this.checkplusDraft[item.quoteId],
        done: () => this.cancel(item),
      })
    },
    onPartialInput(quoteId, value) {
      this.$set(this.partialAmounts, quoteId, value)
    },
    parseAmount(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0
      const normalized = String(value || '').replace(/,/g, '').replace(/[^\d.]/g, '')
      const amount = Number(normalized)
      if (!Number.isFinite(amount)) return 0
      return amount
    },
    canPayPartial(item) {
      const amount = this.parseAmount(this.partialAmounts[item.quoteId])
      return amount > 0 && amount <= Number(item.balance || 0)
    },
    payPartial(item) {
      const amount = this.parseAmount(this.partialAmounts[item.quoteId])
      if (amount <= 0 || amount > Number(item.balance || 0)) return
      this.$emit('pay-partial', { quoteId: item.quoteId, amount })
      this.$delete(this.partialAmounts, item.quoteId)
    },
    overdueLabel(item) {
      if (item?.paid_in_full) return 'Pagada'

      const value = Number(item?.days_overdue)
      if (!Number.isFinite(value)) return '—'
      if (value <= 0) return String(value)
      return `${value} día${value === 1 ? '' : 's'} venc.`
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'

      // Si viene como YYYY-MM-DD (sin hora), parsear manualmente en local
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-').map(Number)
        const date = new Date(y, m - 1, d) // LOCAL, sin shift UTC
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        return `${day}-${month}-${year}`
      }

      // Si trae hora/iso completo, usa Date normal
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return '—'
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    },

    remissionTitle(item) {
      const total = Number(item.total_purchase_orders || 0)
      const done = Number(item.remitted_purchase_orders_count || 0)

      if (item.has_complete_remissions) {
        return `Remisiones completas (${done}/${total} proveedores)`
      }

      if (total <= 0) return 'Sin órdenes de compra para validar remisiones'
      return `Remisiones pendientes (${done}/${total} proveedores)`
    },

  },
}
</script>

<style scoped>
.receivables-table-wrap {
  border: 1px solid #e8e2f0;
  border-radius: 20px;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(106, 92, 133, 0.08), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #fbf9fd 100%);
  box-shadow: 0 16px 34px rgba(67, 51, 92, 0.08);
}

.yc-date-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.yc-date-main {
  display: inline-block;
  font-weight: 600;
  color: #4c4667;
  white-space: nowrap;
}

.yc-money-strong {
  font-weight: 700;
  white-space: nowrap;
}

.yc-money {
  display: inline-block;
  white-space: nowrap;
}

.yc-amount-cell {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 96px;
}

.yc-fiscal-difference {
  display: block;
  max-width: 160px;
  color: #f08a24;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.15;
  text-align: right;
  white-space: normal;
}

.yc-folio-cell {
  display: flex;
  align-items: center;
}

.yc-folio-pill.btn.btn-link {
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #5e5873 0%, #75638f 100%);
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 20px rgba(94, 88, 115, 0.18);
}

.yc-folio-pill.btn.btn-link:hover,
.yc-folio-pill.btn.btn-link:focus {
  color: #fff;
  text-decoration: none;
  transform: translateY(-1px);
}

.yc-aging-pill {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
}

.yc-aging-pill.is-current {
  background: #eef8f1;
  color: #238b57;
}

.yc-aging-pill.is-overdue {
  background: #fff1ef;
  color: #d84d3e;
}

.yc-remission-cell {
  gap: 0.45rem;
}

.yc-remission-text {
  color: #7d7695;
}

.yc-inline-meta,
.yc-invoice-editor,
.yc-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.yc-inline-value,
.yc-file-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.yc-inline-link.btn.btn-link {
  padding: 0;
  color: #75638f;
  font-weight: 600;
  text-decoration: none;
}

.yc-inline-link.btn.btn-link:hover,
.yc-inline-link.btn.btn-link:focus {
  color: #5e5873;
  text-decoration: none;
}

.yc-invoice-input,
.yc-amount-input {
  max-width: 132px;
}

.yc-rep-select-cell {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 150px;
}

.yc-rep-amount-editor {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.yc-rep-amount-input {
  max-width: 150px;
}

.yc-rep-amount-link.btn.btn-link {
  align-self: flex-start;
  font-size: 0.72rem;
}

.yc-rep-amount-link.btn.btn-link:disabled {
  color: #b9b3c7;
}

.yc-rep-amount-warning {
  color: #d84d3e;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.15;
}

.yc-balance-cell {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 0.1rem;
}

.yc-pending-complement {
  color: #f08a24;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
}

.yc-action-btn {
  border-radius: 10px;
  font-weight: 600;
  box-shadow: none;
}

::v-deep .yc-receivables-table {
  margin-bottom: 0;
}

::v-deep .yc-receivables-table thead th {
  background: rgba(117, 99, 143, 0.08);
  border-bottom: 1px solid #e8e2f0;
  color: #6b6484;
  font-size: 0.78rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

::v-deep .yc-receivables-table tbody td {
  padding-top: 0.9rem;
  padding-bottom: 0.9rem;
  border-top: 1px solid #f0ebf5;
  color: #5a5474;
  vertical-align: middle;
}

::v-deep .yc-receivables-table tbody tr:hover {
  background: rgba(117, 99, 143, 0.04);
}

@media (max-width: 991px) {
  .yc-inline-value,
  .yc-file-name {
    max-width: 120px;
  }

  .yc-amount-input,
  .yc-invoice-input {
    max-width: 100%;
  }
}

@media print {
  /* El estado de cuenta impreso es de saldos pendientes: una factura ya cobrada
     que sólo debe su REP no es deuda del cliente. */
  ::v-deep .yc-complement-only-row {
    display: none !important;
  }

  .receivables-table-wrap {
    border: 0;
    border-radius: 0;
    overflow: visible;
    background: #fff;
    box-shadow: none;
  }

  ::v-deep .table-responsive {
    overflow: visible !important;
  }

  ::v-deep .yc-receivables-table {
    table-layout: auto;
    width: 100% !important;
  }

  ::v-deep .yc-receivables-table th,
  ::v-deep .yc-receivables-table td {
    padding: 0.25rem 0.3rem;
  }

  /* Las facturas pueden traer nombres larguísimos (UUID). Se acotan y se parten
     aquí, en vez de forzar table-layout: fixed (que igualaba los anchos y
     encimaba el resto de las columnas). */
  ::v-deep .yc-receivables-table .yc-invoice-file-col {
    max-width: 160px;
  }

  .yc-file-name {
    display: block;
    max-width: 160px;
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.15;
  }
}
</style>
